import {
  DEFAULT_SETTINGS,
  GIF_PROFILES,
  OUTPUT_EDGE_OPTIONS,
  type GifProfileName,
  type OutputEdge,
} from '../constants';

const SETTINGS_KEY = 'webp-lover:settings:v1';
const SHARES_KEY = 'webp-lover:recent-shares:v1';

export type Settings = {
  quality: number;
  outputEdge: OutputEdge;
  gifProfile: GifProfileName;
};

export type RecentShare = {
  url: string;
  deleteToken: string;
  idleExpiresAt: string;
};

export function loadSettings(storage: Storage = localStorage): Settings {
  try {
    const value: unknown = JSON.parse(storage.getItem(SETTINGS_KEY) ?? 'null');
    if (!value || typeof value !== 'object') return { ...DEFAULT_SETTINGS };
    const candidate = value as Partial<Settings>;
    return {
      quality:
        Number.isInteger(candidate.quality) &&
        (candidate.quality ?? 0) >= 1 &&
        (candidate.quality ?? 101) <= 100
          ? (candidate.quality as number)
          : DEFAULT_SETTINGS.quality,
      outputEdge: OUTPUT_EDGE_OPTIONS.includes(candidate.outputEdge as OutputEdge)
        ? (candidate.outputEdge as OutputEdge)
        : DEFAULT_SETTINGS.outputEdge,
      gifProfile:
        candidate.gifProfile && candidate.gifProfile in GIF_PROFILES
          ? candidate.gifProfile
          : DEFAULT_SETTINGS.gifProfile,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: Settings, storage: Storage = localStorage): void {
  storage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

function isRecentShare(value: unknown): value is RecentShare {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<RecentShare>;
  if (
    typeof candidate.url !== 'string' ||
    typeof candidate.deleteToken !== 'string' ||
    typeof candidate.idleExpiresAt !== 'string'
  ) {
    return false;
  }
  try {
    return new URL(candidate.url).protocol === 'https:';
  } catch {
    return false;
  }
}

export function loadRecentShares(storage: Storage = localStorage): RecentShare[] {
  try {
    const value: unknown = JSON.parse(storage.getItem(SHARES_KEY) ?? '[]');
    return Array.isArray(value) ? value.filter(isRecentShare).slice(0, 10) : [];
  } catch {
    return [];
  }
}

export function saveRecentShare(share: RecentShare, storage: Storage = localStorage): void {
  const next = [share, ...loadRecentShares(storage).filter((item) => item.url !== share.url)].slice(
    0,
    10,
  );
  storage.setItem(SHARES_KEY, JSON.stringify(next));
}

export function removeRecentShare(url: string, storage: Storage = localStorage): void {
  storage.setItem(
    SHARES_KEY,
    JSON.stringify(loadRecentShares(storage).filter((share) => share.url !== url)),
  );
}
